export type FloatingRect = {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
};

export type SafeAreaInsets = { top: number; right: number; bottom: number; left: number };

export type FloatingPanelPlacement = {
  left: number;
  top: number;
  maxHeight: number;
  side: "top" | "bottom";
};

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(Math.max(value, minimum), Math.max(minimum, maximum));

export function placeFloatingPanel(options: {
  trigger: FloatingRect;
  panel: { width: number; height: number };
  viewport: { width: number; height: number };
  safeArea?: Partial<SafeAreaInsets>;
  margin?: number;
  gap?: number;
  align?: "start" | "end";
}): FloatingPanelPlacement {
  const margin = Math.max(0, options.margin ?? 8);
  const gap = Math.max(0, options.gap ?? 6);
  const safe = {
    top: Math.max(0, options.safeArea?.top ?? 0),
    right: Math.max(0, options.safeArea?.right ?? 0),
    bottom: Math.max(0, options.safeArea?.bottom ?? 0),
    left: Math.max(0, options.safeArea?.left ?? 0),
  };
  const minimumLeft = safe.left + margin;
  const maximumRight = options.viewport.width - safe.right - margin;
  const minimumTop = safe.top + margin;
  const maximumBottom = options.viewport.height - safe.bottom - margin;
  const maxHeight = Math.max(0, maximumBottom - minimumTop);
  const panelHeight = Math.min(options.panel.height, maxHeight);
  const spaceBelow = maximumBottom - options.trigger.bottom - gap;
  const spaceAbove = options.trigger.top - minimumTop - gap;
  const side = spaceBelow >= panelHeight || spaceBelow >= spaceAbove ? "bottom" : "top";
  const preferredTop = side === "bottom"
    ? options.trigger.bottom + gap
    : options.trigger.top - gap - panelHeight;
  const preferredLeft = options.align === "start"
    ? options.trigger.left
    : options.trigger.right - options.panel.width;

  return {
    left: clamp(preferredLeft, minimumLeft, maximumRight - options.panel.width),
    top: clamp(preferredTop, minimumTop, maximumBottom - panelHeight),
    maxHeight,
    side,
  };
}
